/** Plugin marketplace catalog — publish, browse, install, jobs, delete. */
import { apiDelete, apiGet, apiPost, apiPostForm, type QueryParams } from '../client.js';
import type {
  CatalogEntryOut,
  CatalogKind,
  CatalogPublishResponse,
  InstallJobOut,
} from '../types.js';

export interface ListCatalogParams {
  query?: string;
  /** Narrow by install state. Backend accepts the listed values only. */
  installed?: 'installed' | 'not_installed' | 'upgrade_available';
}

export function listCatalog(
  kind: CatalogKind,
  params: ListCatalogParams = {},
): Promise<CatalogEntryOut[]> {
  return apiGet<CatalogEntryOut[]>(
    `/api/v1/admin/catalog/${kind}`,
    params as QueryParams,
  );
}

export function getCatalogEntry(
  kind: CatalogKind,
  name: string,
): Promise<CatalogEntryOut> {
  return apiGet<CatalogEntryOut>(
    `/api/v1/admin/catalog/${kind}/${encodeURIComponent(name)}`,
  );
}

export interface InstallCatalogArgs {
  name: string;
  version: string;
}

export function installCatalogEntry(args: InstallCatalogArgs): Promise<InstallJobOut> {
  return apiPost<InstallJobOut>('/api/v1/admin/catalog/install', args);
}

export interface PublishSdistArgs {
  sdist: File | Blob;
  sha256: string;
  /**
   * When true the server first deletes any existing release at the
   * same `(name, version)` from pypiserver + catalog, then publishes
   * the new artifact. Without this flag, duplicate uploads 409.
   */
  force?: boolean;
}

export function publishSdist(args: PublishSdistArgs): Promise<CatalogPublishResponse> {
  const form = new FormData();
  form.append('sdist', args.sdist);
  form.append('sha256', args.sha256);
  if (args.force) {
    form.append('force', 'true');
  }
  return apiPostForm<CatalogPublishResponse>('/api/v1/admin/catalog/publish', form);
}

export interface DeleteCatalogVersionArgs {
  kind: CatalogKind;
  name: string;
  version: string;
}

/**
 * Hard-delete a single published version. Removes the sdist file from
 * pypiserver and drops the matching `plugin_catalog` row. Refuses if
 * the version is currently installed in this environment (409).
 */
export function deleteCatalogVersion(args: DeleteCatalogVersionArgs): Promise<void> {
  return apiDelete<void>(
    `/api/v1/admin/catalog/${args.kind}/${encodeURIComponent(args.name)}/${encodeURIComponent(args.version)}`,
  );
}

export interface DeleteCatalogPluginArgs {
  kind: CatalogKind;
  name: string;
}

/**
 * Hard-delete every published version of a plugin. Removes each sdist
 * from pypiserver and drops every matching `plugin_catalog` row in one
 * transaction. Refuses with 409 if the plugin is currently installed
 * (uninstall first).
 */
export function deleteCatalogPlugin(args: DeleteCatalogPluginArgs): Promise<void> {
  return apiDelete<void>(
    `/api/v1/admin/catalog/${args.kind}/${encodeURIComponent(args.name)}`,
  );
}

/**
 * Every install job currently in `pending` or `running` state, newest
 * first. Marketplace cards poll this to surface `Installing…` on every
 * package an in-flight job's `targets` list will install, not only the
 * click target.
 */
export function listActiveInstallJobs(): Promise<InstallJobOut[]> {
  return apiGet<InstallJobOut[]>('/api/v1/admin/install-jobs/active');
}
